import React, { useState, useCallback } from 'react';
import { GoogleGenAI, Type } from '@google/genai';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import Dashboard from './views/Dashboard';
import UploadView from './views/UploadView';
import ReviewView from './views/ReviewView';
import SadFormView from './views/SadFormView';
import SuccessView from './views/SuccessView';
import HistoryView from './views/HistoryView';
import SettingsView from './views/SettingsView';
import LoginView from './views/LoginView';
import { mockHistory } from './constants';
// Updated Initialization
const genAI = new GoogleGenAI({ 
  apiKey: import.meta.env.VITE_GEMINI_API_KEY || '' 
});

const ADMIN_EMAIL = 'dotaonamichael@gmail.com';
const ADMIN_PASSWORD = 'Triple1:8';

const INITIAL_WHITELIST = [
  ADMIN_EMAIL,
  'christinaamos400@gmail.com',
  'navielai802@gmail.com',
  'manambaripetz@gmail.com',
  'mayalisha@gmail.com',
  'Patrickkali425@gmail.com',
  'artuseko25@gmail.com'
];

const App: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);
  
  const [currentView, setCurrentView] = useState<View>('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractedData, setExtractedData] = useState<DeclarationData | null>(null);
  const [sadForm, setSadForm] = useState<DeclarationData | null>(null);
  const [processingStage, setProcessingStage] = useState(0);
  const [history, setHistory] = useState<HistoryEntry[]>(mockHistory);
  const [whitelistedEmails, setWhitelistedEmails] = useState<string[]>(INITIAL_WHITELIST);

  const handleLogin = (email: string, password?: string) => {
    const normalizedEmail = email.toLowerCase().trim();
    const isWhitelisted = whitelistedEmails.map(e => e.toLowerCase()).includes(normalizedEmail);

    if (isWhitelisted) {
      const isAdmin = normalizedEmail === ADMIN_EMAIL.toLowerCase();
      
      if (isAdmin) {
        if (password === ADMIN_PASSWORD) {
          setCurrentUser({ email: normalizedEmail, isAdmin: true });
          setLoginError(null);
        } else {
          setLoginError("Invalid password for Admin account.");
        }
      } else {
        // Normal users log in with just email
        setCurrentUser({ email: normalizedEmail, isAdmin: false });
        setLoginError(null);
      }
    } else {
      setLoginError("Access Denied: This email is not authorized for the trial.");
    }
  };

  const handleLogout = () => {
    setCurrentUser(null);
    setCurrentView('dashboard');
    setUploadedFiles([]);
    setExtractedData(null);
    setSadForm(null);
    setLoginError(null);
  };

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLElement>) => {
    let files: File[] = [];
    if ('dataTransfer' in e) {
       files = Array.from(e.dataTransfer.files);
    } else {
       files = Array.from(e.target.files || []);
    }

    setUploadedFiles(prev => [...prev, ...files.map(f => ({
      name: f.name,
      size: f.size,
      type: f.type,
      file: f
    }))]);
  }, []);
  
  const removeFile = (fileName: string) => {
    setUploadedFiles(prev => prev.filter(f => f.name !== fileName));
  };

  const fileToGenerativePart = async (file: File) => {
    const base64EncodedDataPromise = new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
      reader.readAsDataURL(file);
    });
    return {
      inlineData: {
        data: await base64EncodedDataPromise as string,
        mimeType: file.type,
      },
    };
  };

  const processDocuments = async () => {
    if (uploadedFiles.length === 0) {
      alert("Please upload documents first.");
      return;
    }
    
    setIsProcessing(true);
    setExtractedData(null);
    setProcessingStage(1);

    try {
      const imageParts = await Promise.all(
        uploadedFiles.map(f => fileToGenerativePart(f.file))
      );

      setProcessingStage(2);

      const systemInstruction = `You are an expert customs compliance specialist for Papua New Guinea (PNG). 
      Your task is to extract data from shipping documents and perform tariff classification under the **Customs Tariff Act 2022, Part III – Determination of Tariff Classification**.
      
      **LEGAL FOUNDATION (PNG Law Alignment)**:
      Part III of the Customs Tariff Act explicitly codifies the General Interpretative Rules (GIR) 1–6. You must apply this hierarchy:
      
      1. **GIR 1 (Part III s.8(1))**: Classification determined by terms of headings and Section/Chapter Notes. This is the primary method.
      2. **GIR 2(a) (Part III s.8(2))**: Incomplete/unfinished goods having essential character of complete article; unassembled/disassembled goods.
      3. **GIR 3(a) (Part III s.8(4)(a))**: When goods are classifiable under two or more headings, the most specific description prefers over the general.
      4. **GIR 3(b) (Part III s.8(4)(b))**: Mixtures, composite goods, and sets are classified by the component that gives them their **essential character**.
      5. **GIR 6 (Part III s.13)**: Classification of goods in subheadings (comparison at equal levels).
      
      **SPECIFIC CONSTRAINT FOR HS 2710.12 (Light Oils)**:
      There are NO intermediate numbers between these exact PNG tariff lines. You MUST classify light oils into ONE of these:
      - 2710.12.10: Petrol / Motor Spirit (non-aviation)
      - 2710.12.20: Spirit type Jet Fuel
      - 2710.12.30: Aviation Gasoline (Avgas)
      - 2710.12.40: Jet A1 (Avtur)
      - 2710.12.50: Kerosine, household
      - 2710.12.60: Lubricating oils and greases
      - 2710.12.70: Liquids for hydraulic brakes
      - 2710.12.80: Non-lubricating oils (cleaning, transformer oils)
      - 2710.12.90: Other
      
      **DECISION ENGINE OUTPUT REQUIREMENTS**:
      For each item:
      1. Extract description, weights, values.
      2. Assign the 8-digit HS Code based on PNG Tariff.
      3. **Primary Law**: Always "Customs Tariff Act 2022, Part III".
      4. **Legal Methodology**:
         - List the GIRs applied (e.g., ["1", "6"]).
         - Cite the Part III Clause (e.g., "8(4)(a)" for most specific description).
         - Cite relevant Explanatory Notes (e.g., "EN 85.01").
         - Cite Rulings if applicable.
      5. Provide a 'classificationRationale' explaining the decision (legal audit trail).
      6. Estimate the 'dutyRate'.
      
      Calculate 'totalPGK' using a realistic exchange rate if not provided. Ensure all numbers are pure numbers (no symbols).`;

      const declarationSchema = {
          type: Type.OBJECT,
          properties: {
              exporter: {
                  type: Type.OBJECT,
                  properties: {
                      name: { type: Type.STRING },
                      tin: { type: Type.STRING },
                      address: { type: Type.STRING },
                  },
              },
              importer: {
                  type: Type.OBJECT,
                  properties: {
                      name: { type: Type.STRING },
                      tin: { type: Type.STRING },
                      address: { type: Type.STRING },
                  },
              },
              items: {
                  type: Type.ARRAY,
                  items: {
                      type: Type.OBJECT,
                      properties: {
                          itemNo: { type: Type.NUMBER },
                          description: { type: Type.STRING },
                          commodityCode: { type: Type.STRING }, // This is the HS Code
                          hsCode: { type: Type.STRING }, // Redundant but kept for schema match
                          originCountry: { type: Type.STRING },
                          grossMass: { type: Type.NUMBER },
                          netMass: { type: Type.NUMBER },
                          quantity: { type: Type.NUMBER },
                          unitPrice: { type: Type.NUMBER },
                          totalValue: { type: Type.NUMBER },
                          classificationRationale: { type: Type.STRING, description: "Legal justification for the HS code chosen" },
                          dutyRate: { type: Type.STRING, description: "Applicable duty rate" },
                          primaryLaw: { type: Type.STRING, description: "Customs Tariff Act 2022, Part III" },
                          legalMethodology: {
                              type: Type.OBJECT,
                              properties: {
                                  gir: { type: Type.ARRAY, items: { type: Type.STRING } },
                                  partIIIClause: { type: Type.STRING },
                                  explanatoryNotes: { type: Type.ARRAY, items: { type: Type.STRING } },
                                  rulingsConsidered: { type: Type.ARRAY, items: { type: Type.STRING } }
                              }
                          },
                          girRule: { type: Type.STRING, description: "Legacy field, just put first GIR here" }
                      },
                  },
              },
              shipping: {
                  type: Type.OBJECT,
                  properties: {
                      billOfLading: { type: Type.STRING },
                      vesselName: { type: Type.STRING },
                      arrivalDate: { type: Type.STRING },
                      portOfDischarge: { type: Type.STRING },
                      incoterms: { type: Type.STRING },
                  },
              },
              financial: {
                  type: Type.OBJECT,
                  properties: {
                      totalInvoiceAmount: { type: Type.NUMBER },
                      currency: { type: Type.STRING },
                      exchangeRate: { type: Type.NUMBER },
                      totalPGK: { type: Type.NUMBER },
                  },
              },
          },
      };

      const response = await ai.models.generateContent({
          model: 'gemini-3-flash-preview',
          contents: { parts: [{ text: "Extract and classify these documents." }, ...imageParts] },
          config: {
              systemInstruction: systemInstruction,
              responseMimeType: "application/json",
              responseSchema: declarationSchema,
          },
      });

      setProcessingStage(3);
      
// Access the property directly
const data = JSON.parse(response.text); 
setExtractedData(data);
      setExtractedData(data);
      setCurrentView('review');
    } catch (error) {
      console.error("Error processing documents:", error);
      alert("An AI error occurred while processing the documents. Please check your files or API key and try again.");
    } finally {
      setIsProcessing(false);
      setProcessingStage(0);
    }
  };

  const generateSADForm = () => {
    setSadForm(extractedData);
    setCurrentView('sad-form');
  };

  const addWhitelistedEmail = (email: string) => {
    if (email && !whitelistedEmails.includes(email)) {
      setWhitelistedEmails(prev => [email, ...prev].sort());
    }
  };

  const removeWhitelistedEmail = (emailToRemove: string) => {
    setWhitelistedEmails(prev => prev.filter(email => email !== emailToRemove));
  };

  const generateXML = () => {
    if (!extractedData) return;

    // Enhanced XML structure for ASYCUDA World
    const xmlContent = `<?xml version="1.0" encoding="UTF-8"?>
<ASYCUDA_World_Declaration>
  <General_Segment>
    <Office_Code>POM</Office_Code>
    <Declaration_Type>IM4</Declaration_Type>
    <Reference_Number>DEC-${Date.now()}</Reference_Number>
    <Registration_Date>${new Date().toISOString().split('T')[0]}</Registration_Date>
    <Exporter>
      <Name>${extractedData.exporter.name}</Name>
      <Code>${extractedData.exporter.tin}</Code>
      <Address>${extractedData.exporter.address}</Address>
    </Exporter>
    <Consignee>
      <Name>${extractedData.importer.name}</Name>
      <Code>${extractedData.importer.tin}</Code>
      <Address>${extractedData.importer.address}</Address>
    </Consignee>
    <Transport>
      <Mode>1</Mode>
      <Identity>${extractedData.shipping.vesselName}</Identity>
      <BL_Number>${extractedData.shipping.billOfLading}</BL_Number>
      <Place_of_Loading>FOREIGN PORT</Place_of_Loading>
      <Place_of_Discharge>${extractedData.shipping.portOfDischarge}</Place_of_Discharge>
    </Transport>
    <Financial>
      <Total_Invoice_Amount currency="${extractedData.financial.currency}">${extractedData.financial.totalInvoiceAmount}</Total_Invoice_Amount>
      <Total_CIF_PGK>${extractedData.financial.totalPGK}</Total_CIF_PGK>
      <Exchange_Rate>${extractedData.financial.exchangeRate}</Exchange_Rate>
    </Financial>
  </General_Segment>
  <Items_Segment>
    ${extractedData.items.map(item => `
    <Item>
      <Line_Number>${item.itemNo}</Line_Number>
      <HS_Code>${item.commodityCode}</HS_Code>
      <Description>${item.description}</Description>
      <Origin_Country>${item.originCountry}</Origin_Country>
      <Gross_Mass>${item.grossMass}</Gross_Mass>
      <Net_Mass>${item.netMass}</Net_Mass>
      <Supplementary_Unit>${item.quantity}</Supplementary_Unit>
      <Item_Price>${item.totalValue}</Item_Price>
      <Valuation_Method>1</Valuation_Method>
      <Tariff_Details>
        <Duty_Rate>${item.dutyRate || '0'}</Duty_Rate>
        <GIR_Rule>${item.legalMethodology?.gir.join(',') || item.girRule || '1'}</GIR_Rule>
        <Classification_Note>${item.classificationRationale || ''}</Classification_Note>
        <Authority>${item.primaryLaw || ''} ${item.legalMethodology?.partIIIClause ? `(${item.legalMethodology.partIIIClause})` : ''}</Authority>
      </Tariff_Details>
    </Item>`).join('')}
  </Items_Segment>
</ASYCUDA_World_Declaration>`;

    const blob = new Blob([xmlContent], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ASYCUDA_Export_${Date.now()}.xml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    const newEntry: HistoryEntry = {
      id: history.length + 1,
      date: new Date().toISOString().split('T')[0],
      reference: `DEC-2025-${String(history.length + 1).padStart(3, '0')}`,
      status: 'completed',
      importer: extractedData.importer.name
    };
    setHistory([newEntry, ...history]);
    
    setCurrentView('success');
  };

  const renderView = () => {
    switch (currentView) {
      case 'dashboard':
        return <Dashboard history={history} setCurrentView={setCurrentView} />;
      case 'upload':
        return <UploadView 
                  uploadedFiles={uploadedFiles} 
                  handleFileUpload={handleFileUpload}
                  removeFile={removeFile}
                  isProcessing={isProcessing}
                  processDocuments={processDocuments}
                  processingStage={processingStage}
                />;
      case 'review':
        return <ReviewView extractedData={extractedData} generateSADForm={generateSADForm} />;
      case 'sad-form':
        return <SadFormView sadForm={sadForm} generateXML={generateXML} />;
      case 'success':
        return <SuccessView setCurrentView={setCurrentView} />;
      case 'history':
        return <HistoryView history={history} />;
      case 'settings':
        // Double check admin status before rendering
        if (!currentUser?.isAdmin) return <Dashboard history={history} setCurrentView={setCurrentView} />;
        return <SettingsView 
                  whitelistedEmails={whitelistedEmails} 
                  onAddEmail={addWhitelistedEmail} 
                  onRemoveEmail={removeWhitelistedEmail} 
                />;
      default:
        return <Dashboard history={history} setCurrentView={setCurrentView} />;
    }
  };

  if (!currentUser) {
    return <LoginView onLogin={handleLogin} loginError={loginError} />;
  }

  return (
    <div className="min-h-screen bg-gray-900 text-gray-300 font-sans">
      <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} userEmail={currentUser.email} onLogout={handleLogout} />
      <div className="flex">
        <Sidebar currentView={currentView} setCurrentView={setCurrentView} sidebarOpen={sidebarOpen} isAdmin={currentUser.isAdmin} />
        <main className="flex-1 p-6 lg:p-8 overflow-auto">
          {renderView()}
        </main>
      </div>
    </div>
  );
};

export default App;
